import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import Hero from "@/components/home/Hero";
import Statement from "@/components/home/Statement";
import Emotion from "@/components/home/Emotion";
import FeaturedProperties from "@/components/home/FeaturedProperties";
import WhyUs from "@/components/home/WhyUs";
import Adventure from "@/components/common/Adventure";
import Header from "@/components/layout/Header";
import ReviewCarousel from "@/components/home/ReviewCarousel";
import RecentlySoldSection from "@/components/home/RecentlySoldSection";

const Home: React.FC = () => {
  const { i18n } = useTranslation();

  const lang = (i18n.language?.split("-")[0] || "en").toLowerCase();
  const isES = lang === "es";
  const isFR = lang === "fr";

  const pageTitle = isES
    ? "DEM Properties | Asesoría inmobiliaria en Marbella"
    : isFR
    ? "DEM Properties | Conseil immobilier à Marbella"
    : "DEM Properties | Real Estate Advisory in Marbella";

  const metaDescription = isES
    ? "Asesoría inmobiliaria en Marbella. Selección de propiedades, oportunidades off-market y representación personalizada."
    : isFR
    ? "Conseil immobilier à Marbella. Sélection de biens, opportunités off-market et accompagnement sur mesure."
    : "Real estate advisory based in Marbella. Curated properties, off-market opportunities and bespoke representation.";

  const metaKeywords = isES
    ? "inmobiliaria Marbella, propiedades, off-market, Costa del Sol, dem properties"
    : isFR
    ? "immobilier Marbella, propriétés, off-market, Costa del Sol, dem properties"
    : "real estate Marbella, properties, off-market properties, Costa del Sol, dem properties";

  const ogLocale = isES ? "es_ES" : isFR ? "fr_FR" : "en_GB";

  return (
    <div className="w-full overflow-hidden">
      <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />

        <link rel="canonical" href="https://demproperties.es/" />

        {/* Open Graph */}
        <meta property="og:title" content="DEM Properties — Marbella" />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content="https://demproperties.es/" />
        <meta property="og:image" content="https://demproperties.es/og-image-home.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            name: "DEM Properties",
            image: "https://demproperties.es/logo.webp",
            "@id": "https://demproperties.es",
            url: "https://demproperties.es",
            telephone: "+34655623860",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Marbella",
              addressLocality: "Marbella",
              addressRegion: "Málaga",
              postalCode: "29600",
              addressCountry: "ES",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 36.51007,
              longitude: -4.88244,
            },
            sameAs: [
              "https://www.instagram.com/dounia_elmoutii_properties_?igsh=dWtudnlreHlqYXdh",
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <Hero />
      <Statement />
      <Emotion />
      <FeaturedProperties />
      <WhyUs />
      <ReviewCarousel />
      <RecentlySoldSection />
      <Adventure />
    </div>
  );
};

export default Home;
